/**
 * Loading and Error State Components
 */

import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from './button'
import { Spinner } from './spinner'

interface LoadingStateProps {
  message?: string
}

export function LoadingState({ message = 'Loading...' }: LoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Spinner className="h-8 w-8 text-indigo-600" />
      <p className="mt-4 text-sm text-gray-500">{message}</p>
    </div>
  )
}

interface ErrorStateProps {
  error?: Error | null
  title?: string
  message?: string
  onRetry?: () => void
}

export function ErrorState({
  error,
  title,
  message = 'Failed to load data',
  onRetry,
}: ErrorStateProps) {
  // Support both patterns: title as heading, or message as heading with error.message as detail
  const heading = title || message
  const detail = title ? message : error?.message

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="rounded-full bg-red-100 p-3">
        <AlertCircle className="h-6 w-6 text-red-600" />
      </div>
      <h3 className="mt-4 text-lg font-medium text-gray-900">{heading}</h3>
      {detail && (
        <p className="mt-1 text-sm text-gray-500">{detail}</p>
      )}
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4 gap-2"
          onClick={onRetry}
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </div>
  )
}

interface EmptyStateProps {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
  }
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {icon && <div className="mb-4">{icon}</div>}
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}
      {action && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}
